<template>
  <form
    class="donor-form-card"
    method="POST"
    target="_blank"
    @submit.prevent="submitPersonalDataForm"
  >
    <div class="donor-form-card__body">
      <div class="donor-form-header">
        <div class="donor-form-header__icon">
          <UserIcon class="h-5 w-5 text-primaryAccent" aria-hidden="true" />
        </div>
        <div class="donor-form-header__text">
          <h2 class="donor-form-title">Personal Details</h2>
          <p class="donor-form-subtitle">Tell us a little about yourself to get started.</p>
        </div>
      </div>

      <section class="donor-form-section">
        <p class="donor-form-section-title">Your name</p>
        <div class="donor-form-grid">
          <InputField
            label-size="small"
            input-text-size="small"
            v-model:value="form.firstName.value"
            :errors="form.firstName.errors"
            @blur="form.firstName.validate()"
            @input="onNameInput($event, 'firstName')"
            label="First name"
            type="text"
            text="first-name"
            id="first-name"
            placeholder="First name"
            autocomplete="given-name"
          />
          <InputField
            label-size="small"
            input-text-size="small"
            v-model:value="form.lastName.value"
            :errors="form.lastName.errors"
            @blur="form.lastName.validate()"
            @input="onNameInput($event, 'lastName')"
            label="Last name"
            type="text"
            text="last-name"
            id="last-name"
            placeholder="Last name"
            autocomplete="family-name"
          />
        </div>
      </section>

      <section class="donor-form-section donor-form-section-divider">
        <p class="donor-form-section-title">Date of birth</p>
        <div class="donor-form-grid-thirds">
          <SelectField
            small
            :select-options="monthData"
            v-model:value="form.birthMonth.value"
            :errors="form.birthMonth.errors"
            label="Month"
          />
          <InputField
            label-size="small"
            input-text-size="small"
            v-model:value="form.birthDay.value"
            :errors="form.birthDay.errors"
            @blur="form.birthDay.validate()"
            label="Day"
            type="number"
            text="birth-day"
            id="birth-day"
            placeholder="DD"
          />
          <SelectField
            small
            :select-options="years"
            v-model:value="form.birthYear.value"
            :errors="form.birthYear.errors"
            label="Year"
          />
        </div>
      </section>
    </div>

    <div class="donor-form-actions">
      <BaseButton
        v-if="showClearForm"
        type="button"
        variant="secondary"
        @click="$emit('clearForm')"
      >
        Clear Form
      </BaseButton>
      <BaseButton
        type="submit"
        id="personal-data-submit"
        icon-position="trailing"
        :icon="ArrowRightIcon"
      >
        Next Step
      </BaseButton>
    </div>
  </form>
  <Modal
    :headerText="`You need to be at least ${lowestAge} years old to donate.`"
    ctaText="Sorry about that!"
    :modalOpen="tooYoungModalOpen"
    @closeModal="toggleTooYoungModal"
    :showFailureIcon="true"
  />
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { ArrowRightIcon, UserIcon } from '@heroicons/vue/20/solid'
import SelectField from '@/components/inputs/SelectField'
import InputField from '@/components/inputs/InputField'
import { PersonalDonorForm } from '@/services/users/'
import monthData from '@/data/monthData.js'
import Modal from '@/components/Modal'
import { useRoute } from 'vue-router'
import { sanitizeName } from '@/utils/misc'

export default {
  name: 'DonorFormPersonal',
  components: {
    SelectField,
    InputField,
    Modal,
    UserIcon,
    ArrowRightIcon,
  },
  emits: ['personalData', 'stepNumber', 'userAge', 'clearForm'],
  props: {
    lowestAge: [Number, String],
    showClearForm: Boolean,
    initialValues: {
      type: Object,
      default: null,
    },
  },
  setup(props, { emit }) {
    const form = ref(new PersonalDonorForm())

    const applyInitialValues = (values) => {
      if (!values || !Object.keys(values).length) return
      form.value = PersonalDonorForm.create({ ...values })
    }

    watch(() => props.initialValues, applyInitialValues, { immediate: true, deep: true })
    const submitButton = ref(null)
    const currentYear = new Date().getFullYear()
    const years = computed(() => {
      return Array.from({ length: currentYear - 1900 }, (value, index) => {
        const year = 1901 + index
        return { value: year, text: `${year}` }
      }).reverse()
    })
    const birthYear = ref(currentYear)
    const tooYoungModalOpen = ref(false)
    const donorLat = ref(null)
    const donorLong = ref(null)
    const route = useRoute()

    onMounted(() => {
      submitButton.value = document.querySelector('#personal-data-submit')
      if (route.query.from_qrcode === 'true') {
        applyQrScanLocation()
      } else if (!route.query.resubmit) {
        getLocation()
      }
    })

    function onNameInput(event, fieldName) {
      const sanitized = sanitizeName(event.target.value)
      if (event.target.value !== sanitized) {
        event.target.value = sanitized
      }
      form.value[fieldName].value = sanitized
    }

    function applyQrScanLocation() {
      const lat = parseFloat(route.query.lat)
      const lng = parseFloat(route.query.lng)
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        form.value.latitude.value = lat
        form.value.longitude.value = lng
      }
    }

    function getLocation() {
      if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition((position) => {
          form.value.latitude.value = position.coords.latitude
          form.value.longitude.value = position.coords.longitude
        })
      } else {
        console.log('Geolocation not available by this browser.')
      }
    }

    function submitPersonalDataForm() {
      const unwrappedForm = form.value

      Object.keys(unwrappedForm).forEach((key) => {
        if (unwrappedForm[key]?.value && typeof unwrappedForm[key].value === 'string') {
          unwrappedForm[key].value = unwrappedForm[key].value.trim().replace(/\s+/g, ' ')
        }
      })

      unwrappedForm.validate()
      if (!unwrappedForm.isValid) return

      if (handleAgeCheck() === false) {
        toggleTooYoungModal()
        return
      }

      emit('personalData', form.value.formVal)
      emit('stepNumber', 1)
      emit('userAge', calculateAge())
    }

    function toggleTooYoungModal() {
      tooYoungModalOpen.value = !tooYoungModalOpen.value
    }

    function calculateAge() {
      const unwrappedForm = form.value
      const today = new Date()

      const birthDate = new Date(
        unwrappedForm.birthYear.value,
        unwrappedForm.birthMonth.value - 1,
        unwrappedForm.birthDay.value,
      )

      let age = today.getFullYear() - birthDate.getFullYear()

      const monthDiff = today.getMonth() - birthDate.getMonth()
      const dayDiff = today.getDate() - birthDate.getDate()

      if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
        age--
      }

      return age
    }

    function handleAgeCheck() {
      if (calculateAge() < props.lowestAge) {
        return false
      }
    }

    return {
      form,
      submitButton,
      birthYear,
      monthData,
      years,
      tooYoungModalOpen,
      submitPersonalDataForm,
      toggleTooYoungModal,
      onNameInput,
      donorLat,
      donorLong,
      ArrowRightIcon,
    }
  },
}
</script>
